import { useCallback, useRef, useState } from 'react';
import { agreementsApi } from '@/modules/agreements/services/api';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';
import type { InteractiveSigningMethod } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';

/**
 * The signing session, opened on demand.
 *
 * The SERVER issues the challenge and binds the content hash. The client never invents a
 * challenge: every stage that names a session — the signature upload, the step-up ceremony
 * and the final sign — carries the challenge returned here.
 *
 * The session is opened lazily, the first time a stage needs it, and then reused. Opening it
 * in an effect would fire a request on every render pass and would race the user's first
 * action; opening it on demand keeps the request tied to the action that needs it.
 */
export function useSigningSession(executionId: string) {
  const [session, setSession] = useState<SigningSessionResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const pending = useRef<Promise<SigningSessionResponse> | null>(null);

  const ensureSession = useCallback(
    async (method: InteractiveSigningMethod): Promise<SigningSessionResponse> => {
      if (session) return session;
      if (pending.current) return pending.current;
      setError(null);
      const request = agreementsApi
        .openSigningSession(executionId, { signingMethod: method, authorityDeclared: true })
        .then((opened) => {
          setSession(opened);
          return opened;
        })
        .catch((caught: unknown) => {
          const failure = caught instanceof Error ? caught : new Error(String(caught));
          setError(failure);
          throw failure;
        })
        .finally(() => {
          pending.current = null;
        });
      pending.current = request;
      return request;
    },
    [executionId, session],
  );

  return { session, error, ensureSession };
}
